/**
 * Otto Views wire types — the versioned envelope the backend persists
 * (`views/blocks.py`) plus the response metadata.
 *
 * Spec v3 is the definition-driven ordered-blocks vocabulary (verdict,
 * stat_row, table, stage_strip, attention_list, narrative, chart). It is the
 * ONLY renderable envelope: an artifact persisted under an older spec version
 * is treated as a cache miss server-side and regenerated on read. The envelope
 * is kept in wire shape (snake_case fields) on purpose: it is a versioned spec contract
 * shared with persistence, not a REST resource to be re-cased. Response-level
 * metadata is converted to camelCase like every other client.
 */

/**
 * The closed severity vocabulary — a four-step **risk** scale. Additive over
 * the original three (`good` was already reachable in the renderer from a local
 * stage-status mapping; the model can now emit it directly).
 *
 * `attention_list` items deliberately stay on the three-value risk subset — a
 * green "On track" chip inside a block titled *Needs attention* inverts the
 * fail-loud default the server applies there.
 */
export type BriefChipTone = 'calm' | 'good' | 'watch' | 'late';

/* eslint-disable @typescript-eslint/naming-convention */

export type BriefEvidence = {
  /** Verbatim excerpt, present only when server-verified against its source. */
  quote?: string;
  /** Computed server-side: the quote matched the cited REF's exact text. */
  verified: boolean;
  gloss?: string;
  ref?: string;
  date?: string;
};

export type BriefStageStatus =
  | 'complete'
  | 'active'
  | 'blocked'
  | 'pending'
  | 'no_evidence';

export type BriefStage = {
  name: string;
  status: BriefStageStatus;
  /** Evidence-backed meaning of `window` (e.g. "Target window"). */
  timing_label?: string;
  window?: string;
  delta?: string;
  note?: string;
  /** Optional and additive: artifacts persisted before this field existed have
   * no `evidence`/`derived` on their stages — treat both as absent, not `[]`/
   * `false`, until the artifact next regenerates. */
  evidence?: BriefEvidence[];
  derived?: boolean;
};

/* -- Spec v3: definition-driven ordered blocks ------------------------------ */

export type ViewBlockWidth = 'full' | 'half';

/** Common fields the validator attaches to every block (definition-authoritative).
 *
 * `tone`/`emphasis`/`capped` are the block-level severity dials (Dial 3), read
 * once in `ViewBlockRenderer` rather than per block type — so `verdict`,
 * `narrative` and `chart` get severity through the same mechanism as the
 * item-level blocks. All three are optional and additive: an envelope cached
 * before they existed renders unchanged.
 */
type ViewBlockBase = {
  block_id: string;
  width: ViewBlockWidth;
  title?: string;
  /** Server-computed fingerprint of the Definition entry this block was filled
   *  from — the input to the backend's per-block staleness comparison. Carried
   *  for round-tripping only; nothing in the UI reads it (the server hands the
   *  conclusion down as `stale_block_ids`). */
  entry_hash?: string;
  /** Severity for the block as a whole — recolors the block's own title
   *  text (see `BlockShell`'s `tone` prop in `ViewBlocks.tsx`). One rendering
   *  for every block type: no stripe, no wash — `table`/`stat_row`/
   *  `attention_list`/`stage_strip` already color tone at the row/tile/item
   *  grain, so this is reinforcement there rather than a second signal. */
  tone?: BriefChipTone;
  /** Importance, distinct from severity. Persisted only when `hero`; the
   *  server demotes ALL heroes when more than one block claims it (no accent
   *  beats a wrong accent). Renders as a gradient rail abutting the block's
   *  own card (`HERO_RAIL`/`HeroRail` in `ViewBlocks.tsx`) — the same device
   *  `verdict` has always shown unconditionally, generalized to every other
   *  block type and gated on this field. */
  emphasis?: 'hero';
  /** The block left content out to fit a cap — model-declared, and forced true
   *  server-side when the validator's own truncation ran. Renders as a derived
   *  "More than shown" footer, deliberately without a count. */
  capped?: boolean;
};

export type ViewStatTile = {
  label: string;
  value: string;
  /** Computed server-side: every numeric token matched a server fact. Absent
   * entirely (not `false`) on tiles from an `identity: true` stat_row, where
   * the verified/derived mechanism doesn't apply — there's nothing to check a
   * name or a date against. */
  verified?: boolean;
  /** Computed server-side: `!verified` — renders the "Otto's read" marker. */
  derived?: boolean;
  /** Optional one-sentence interpretation of the value, never source identity.
   * Source labels and click-through resolve deterministically from `refs`. */
  caption?: string;
  tone?: BriefChipTone;
  /** Resolved supporting sources. A single source renders as the source label;
   * a multi-source tile opens a source picker. */
  refs?: string[];
  /** Backward compatibility for View artifacts generated before `refs`. */
  ref?: string;
};

/** Closed set — absent means `text`. `number`/`duration` right-align with
 *  tabular numerals so digits line up down the column; the unit lives in the
 *  column `label` ("Turnaround (days)"), never baked into the cell value. */
export type ViewTableColumnType =
  | 'text'
  | 'number'
  | 'date'
  | 'duration'
  | 'enum';

export type ViewTableColumn = {
  key: string;
  label: string;
  type?: ViewTableColumnType;
  /** For `type === 'enum'`: the possible values and their optional tones.
   * `fill` is LLM-authored chip weight for a binary (exactly 2 values),
   * non-primary column — 'solid' draws the eye, 'outline' recedes. */
  enum_values?: Array<{
    value: string;
    tone?: BriefChipTone;
    fill?: 'solid' | 'outline';
  }>;
  /** True if this is the primary categorical column for the table. At most one
   * column per table should have this set to true. When present, the row's tone
   * indicator is derived from this column's cell value instead of `row.tone`. */
  is_primary_category?: boolean;
};

export type ViewTableRow = {
  cells: Record<string, string>;
  evidence: BriefEvidence[];
  /** Computed server-side: true iff nothing on the row cites a real source. */
  derived: boolean;
  tone?: BriefChipTone;
  /**
   * Per-cell severity, keyed by column key — a sparse sidecar, not a union on
   * the cell value, so `cells` stays `Record<string, string>` everywhere.
   * Renders as colored cell text (never a pill).
   */
  cell_tones?: Record<string, BriefChipTone>;
  /**
   * Categorical relevance — "this row belongs to a set the reader cares
   * about" — deliberately **orthogonal to `tone`**. Tone is a risk scale, so a
   * calm-but-relevant row (e.g. a commitment owned by us that isn't late) can
   * never be expressed through it; that gap is why this exists.
   *
   * A different value from the block-level `emphasis: 'hero'` on purpose: hero
   * is scarce and demoted when contested, whereas many rows are expected to
   * carry `strong` at once (marking a whole category is the point).
   */
  emphasis?: 'strong';
};

export type ViewAttentionItem = {
  title: string;
  tone: BriefChipTone;
  evidence: BriefEvidence[];
  derived: boolean;
  /** Optional consequence or next decision, not a repeat of the title/evidence. */
  note?: string;
  /** Reserved for future use; currently visually inert. */
  emphasis?: 'strong';
};

/**
 * Chart payload — mirrors the server `_validate_chart` output (blocks.py) and
 * the Otto chat viz shapes (`chat/components/viz/vizSpec.ts`), so a `chart`
 * block adapts into a `VizSpec` and renders through the same `OttoVizBlock`
 * Recharts renderer. Data is model-synthesized, hence always `derived`.
 */
export type ViewChartDatum = {label: string; value: number};

export type ViewChartAxisFormat =
  | 'date-short'
  | 'date-month'
  | 'number'
  | 'number-compact'
  | 'currency'
  | 'percent'
  | 'duration-days';

export type ViewChartAxis = {
  type?: 'category' | 'number' | 'date';
  format?: ViewChartAxisFormat;
  label?: string;
};

export type ViewChartCartesianSeries = {
  name: string;
  encoding: 'bar' | 'line' | 'area';
  stack_group?: string;
  points: Array<{x: string | number; y: number}>;
};

export type ViewChartPayload =
  | {
      chart_type: 'cartesian';
      x_axis?: ViewChartAxis;
      y_axis?: ViewChartAxis;
      series: ViewChartCartesianSeries[];
      legend_position?: 'top-right' | 'top' | 'bottom';
      horizontal?: boolean;
      /** 'ordinal' ramps series colors light-to-dark in series order — for
       * genuinely ordered series (tiers/levels), never unordered categories. */
      color_scale?: 'categorical' | 'ordinal';
      /** 'percent' renormalizes each stacked column to sum to 100% at render
       * time — the model always emits raw counts/values, never percentages. */
      normalize?: 'percent';
      /** Optional one-sentence takeaway — same anti-filler discipline as a
       * stat_row tile's caption. */
      insight?: string;
    }
  | {chart_type: 'donut'; data: ViewChartDatum[]; insight?: string};

/**
 * One TLDR statement, plus the block a reader should open to see the working.
 *
 * `block_id` names another block in the SAME envelope — the only cross-block
 * reference in the vocabulary. The server guarantees it resolves to a block
 * that actually rendered (`blocks.py::_prune_tldr_anchors`), so the click
 * target exists for any freshly generated View; a cached envelope whose
 * Definition was edited without regenerating can still go stale, which is why
 * the click is null-safe rather than assuming a hit.
 *
 * No evidence and no tone by design: a line points AT a block, and that block
 * carries the grounding.
 */
export type ViewTldrLine = {
  block_id: string;
  text: string;
};

export type ViewBlock =
  | (ViewBlockBase & {type: 'verdict'; text: string; derived: boolean})
  | (ViewBlockBase & {
      type: 'stat_row';
      tiles: ViewStatTile[];
      /** Turns the row into a compact scope-identity header (e.g. customer,
       * program, start date) instead of a numeric "key figures" row. Tiles in
       * this block never carry `verified`/`derived`. */
      identity?: boolean;
    })
  | (ViewBlockBase & {
      type: 'table';
      columns: ViewTableColumn[];
      rows: ViewTableRow[];
    })
  | (ViewBlockBase & {type: 'stage_strip'; stages: BriefStage[]})
  | (ViewBlockBase & {type: 'attention_list'; items: ViewAttentionItem[]})
  | (ViewBlockBase & {
      type: 'narrative';
      text: string;
      evidence: BriefEvidence[];
      derived: boolean;
    })
  | (ViewBlockBase & {type: 'chart'; derived: boolean} & ViewChartPayload)
  | (ViewBlockBase & {type: 'tldr'; lines: ViewTldrLine[]});

/**
 * One blind spot of the View as a whole — what the corpus does not contain
 * (`missing`) and the conclusion that is therefore blocked (`impact`). Server-
 * grounded: the generator may only report gaps that were declared in guidance,
 * stated in the prompt's coverage section, or backed by its own abstentions.
 *
 * Distinct from a block's `capped`, which says "I left items out of THIS
 * block" — coverage is a statement about the View's ability to see at all.
 */
export type ViewCoverageGap = {missing: string; impact: string};
export type ViewCoverage = {gaps: ViewCoverageGap[]};

export type ViewEnvelopeV3 = {
  spec_version: number;
  blocks: ViewBlock[];
  /** Deterministic no-content envelope (empty scope, zero LLM). */
  empty?: {message: string};
  /** Additive optional key — absent on artifacts cached before it shipped, and
   * on any scope with nothing grounded to disclose. */
  coverage?: ViewCoverage;
  /** True only on a seeded shell — a view that has never run a full
   * generation but has had one or more blocks written individually. A missing
   * definition entry means "never written" (Instruction face) here, vs.
   * "deliberately omitted" (Empty face) once this is absent/false. A full
   * generation never sets this; an explicit regenerate clears it. */
  partial?: boolean;
};

export type ViewEnvelope = ViewEnvelopeV3;

/** Runtime discriminator — shape-based, so an envelope that somehow predates
 * the current spec renders as nothing instead of crashing the tab. */
export function isV3Envelope(
  envelope: ViewEnvelope
): envelope is ViewEnvelopeV3 {
  return envelope.spec_version >= 3 && Array.isArray(envelope.blocks);
}

/** Resolved `REF-N` target for click-through. */
export type BriefRefTarget = {
  kind: 'thread' | 'message' | 'signal' | 'file' | 'note' | 'record';
  label: string;
  thread_id?: string | null;
  message_id?: string | null;
  signal_id?: string | null;
  file_id?: string | null;
  note_id?: string | null;
  // Aggregate (portfolio) Views only: contextual identity for a member record —
  // which record a snippet belongs to. A `record`-kind ref carries the record's
  // own field text as evidence and never navigates anywhere.
  type_id?: string | null;
  record_id?: string | null;
  /** Server-known (not model-asserted) — for the hover peek's Level 1 (design
   *  otto-views-canvas.md §5/§11.1). Absent for kinds with no natural sender
   *  (thread/signal/file/record). */
  sender?: string | null;
  /** Server-known occurrence date, ISO `YYYY-MM-DD`. Prefer this over
   *  `BriefEvidence.date` (model-asserted, unverified) once present. */
  occurred_at?: string | null;
};
/* eslint-enable @typescript-eslint/naming-convention */

/**
 * The concrete in-thread item a citation points at: what to highlight on
 * click-through, and what "Correct with note" anchors its composer to. `thread`
 * refs and refs missing their id resolve to `undefined` — there's a thread to
 * open but no row to land on.
 *
 * Exhaustive over `kind` on purpose: a new ref kind added by the backend
 * becomes a compile error here rather than a silently dead affordance.
 */
export function citedThreadItem(
  target: BriefRefTarget
): {itemId: string; sourceKind: 'message' | 'signal' | 'note'} | undefined {
  switch (target.kind) {
    case 'signal': {
      return target.signal_id
        ? {itemId: target.signal_id, sourceKind: 'signal'}
        : undefined;
    }

    case 'note': {
      return target.note_id
        ? {itemId: target.note_id, sourceKind: 'note'}
        : undefined;
    }

    case 'thread':
    case 'message':
    case 'file': {
      return target.message_id
        ? {itemId: target.message_id, sourceKind: 'message'}
        : undefined;
    }

    case 'record': {
      // A record citation is contextual identity only — it names the member
      // record a snippet belongs to. There is no in-thread row to land on, and
      // no navigation target at all.
      return undefined;
    }
  }
}

export type ViewResult = {
  scopeKey: string;
  definitionId: string;
  definitionName: string;
  stale: boolean;
  generated: boolean;
  specVersion: number;
  generatedAt: string;
  view: ViewEnvelope;
  refs: Record<string, BriefRefTarget>;
  steerUpdatedAt?: string;
  /**
   * Blocks whose OWN Definition instructions changed since that block was last
   * filled — per-block staleness, computed server-side by comparing each
   * block's `entry_hash` against the current Definition entry. Narrower than
   * the envelope-level `stale` flag (which any fingerprint input can trip), and
   * present only on the full View response, never on the status response.
   */
  staleBlockIds: string[];
};

export type ViewStatus = {
  scopeKey: string;
  definitionId: string;
  definitionName: string;
  generated: boolean;
  stale: boolean;
  generatedAt?: string;
  steerUpdatedAt?: string;
};

export type ViewInstanceSummary = {
  scopeKey: string;
  definitionId: string;
  generatedAt: string;
};

export type ViewSteer = {
  scopeKey: string;
  definitionId: string;
  instructions: string;
  updatedAt?: string;
  updatedBy?: string;
};

/* -- View Definitions (templates) ------------------------------------------- */

export type ViewBlockEntry = {
  id: string;
  blockType: ViewBlock['type'];
  width: ViewBlockWidth;
  title?: string;
  prompt: string;
};

/** Discriminates a record-scope Definition from a type-wide (portfolio) one. */
export type ViewScopeMode = 'records' | 'type';

export type ViewDefinition = {
  id: string;
  name: string;
  blocks: ViewBlockEntry[];
  isBuiltin: boolean;
  scopeMode: ViewScopeMode;
  /** The scope this Definition was created in; undefined = unscoped/legacy
   * (visible in every scope's catalog — see `useViewDefinitions`). */
  originScopeKey?: string;
  /** Explicitly promoted to a cross-scope template — visible everywhere
   * regardless of `originScopeKey`. */
  isShared: boolean;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
};

/** A stock starting point (GET /views/definitions/templates). */
export type ViewTemplate = {
  key: string;
  name: string;
  /** One line on what the template is for — what the picker shows under the
   *  name, in place of a list of the block types it happens to contain. */
  description: string;
  /** Which scope surface the template belongs to — the gallery offers a record
   *  scope only 'records' templates and a type bucket only 'type' templates. */
  scopeMode: 'records' | 'type';
  blocks: ViewBlockEntry[];
};

/** What the builder's `view_definition_updated` SSE event says changed —
 * mirrors the backend's `CHANGE_KINDS` (builder_capabilities.py). */
export const VIEW_DEFINITION_CHANGE_KINDS = [
  'create',
  'add',
  'update',
  'remove',
  'reorder',
  'rename',
  'template',
] as const;

export type ViewDefinitionChangeKind =
  (typeof VIEW_DEFINITION_CHANGE_KINDS)[number];

export type ViewDefinitionChange = {
  kind: ViewDefinitionChangeKind;
  /** Block ids affected by the change — drives the canvas flash only. */
  blockIds: string[];
};

/* -- Plan checkpoint (grounded-discovery build confirmation) ---------------- */

/** One proposed block in a plan checkpoint — a not-yet-real block Otto intends
 *  to build, described by its type + purpose. */
export type PlanCheckpointBlock = {blockType: string; purpose: string};

/**
 * Otto's proposed build plan for a new View, surfaced on the canvas as a
 * confirmation card after the discovery conversation. Ephemeral (not
 * persisted) — the user either confirms ("Build it") or hands back an
 * adjustment note, both routed through the builder rail's send channel.
 */
export type PlanCheckpoint = {
  viewName: string;
  blocks: PlanCheckpointBlock[];
  summary?: string;
};
